import type { BackofficePermissionCode, MerchantEmployeeStatus, MerchantRole } from './auth';
import type { PromotionType, PromotionStatus } from './promotion';
import type { MerchantStatus } from './merchant';
import type { LeadStatus } from './lead';

export const PROMOTION_TYPE_LABELS: Record<PromotionType, string> = {
  DISCOUNT: 'Desconto',
  CASHBACK: 'Cashback',
  LOYALTY: 'Fidelidade',
  FREEBIE: 'Brinde',
};

export const PROMOTION_STATUS_LABELS: Record<PromotionStatus, string> = {
  DRAFT: 'Rascunho',
  PENDING_REVIEW: 'Em análise',
  ACTIVE: 'Ativo',
  PAUSED: 'Pausado',
  EXPIRED: 'Expirado',
  REJECTED: 'Rejeitado',
};

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  NEW: 'Novo',
  CONTACTED: 'Contatado',
  QUALIFIED: 'Qualificado',
  NEGOTIATING: 'Negociando',
  WON: 'Ganho',
  LOST: 'Perdido',
};

export const MERCHANT_STATUS_LABELS: Record<MerchantStatus, string> = {
  ACTIVE: 'Ativo',
  PENDING: 'Pendente',
  SUSPENDED: 'Suspenso',
  INACTIVE: 'Inativo',
};

export const BACKOFFICE_PERMISSION_LABELS: Record<BackofficePermissionCode, string> = {
  ROLE_BACKOFFICE_DASHBOARD_READ: 'Ver dashboard',
  ROLE_BACKOFFICE_MERCHANT_READ: 'Ver lojistas',
  ROLE_BACKOFFICE_MERCHANT_MANAGE: 'Gerenciar lojistas',
  ROLE_BACKOFFICE_CUSTOMER_READ: 'Ver usuários',
  ROLE_BACKOFFICE_CUSTOMER_MANAGE: 'Gerenciar usuários',
  ROLE_BACKOFFICE_CATEGORY_MANAGE: 'Gerenciar categorias',
  ROLE_BACKOFFICE_PROMOTION_MODERATE: 'Moderar anúncios',
  ROLE_BACKOFFICE_EXPERIENCE_MODERATE: 'Moderar experiências',
  ROLE_BACKOFFICE_LOCALITY_READ: 'Ver localidades',
  ROLE_BACKOFFICE_LOCALITY_MANAGE: 'Gerenciar localidades',
  ROLE_BACKOFFICE_AMENITY_READ: 'Ver comodidades',
  ROLE_BACKOFFICE_AMENITY_MANAGE: 'Gerenciar comodidades',
  ROLE_BACKOFFICE_PAYMENT_METHOD_READ: 'Ver formas de pagamento',
  ROLE_BACKOFFICE_PAYMENT_METHOD_MANAGE: 'Gerenciar formas de pagamento',
  ROLE_BACKOFFICE_SEARCH_TERM_READ: 'Ver termos buscados',
  ROLE_BACKOFFICE_FEED_READ: 'Ver matérias',
  ROLE_BACKOFFICE_FEED_MANAGE: 'Gerenciar matérias',
  ROLE_BACKOFFICE_COLLECTION_READ: 'Ver coleções',
  ROLE_BACKOFFICE_COLLECTION_MANAGE: 'Gerenciar coleções',
  ROLE_BACKOFFICE_LEAD_READ: 'Ver leads',
  ROLE_BACKOFFICE_LEAD_MANAGE: 'Gerenciar leads',
  ROLE_BACKOFFICE_MESSAGING_READ: 'Ver disparos',
  ROLE_BACKOFFICE_MESSAGING_MANAGE: 'Gerenciar disparos',
  ROLE_BACKOFFICE_MESSAGING_SEND: 'Disparar mensagens',
  ROLE_BACKOFFICE_PARTNERSHIP_MANAGE: 'Gerenciar parceria',
  ROLE_BACKOFFICE_CLAIM_READ: 'Ver reivindicações',
  ROLE_BACKOFFICE_CLAIM_MANAGE: 'Decidir reivindicações',
  ROLE_BACKOFFICE_CLAIM_EVIDENCE_READ: 'Abrir documentos anexados',
  ROLE_BACKOFFICE_PROFILE_EDIT_MODERATE: 'Moderar edições do responsável',
  ROLE_BACKOFFICE_PLAN_MANAGE: 'Gerenciar planos',
  ROLE_BACKOFFICE_PASS_PLAN_MANAGE: 'Gerenciar Kardume Pass',
  ROLE_BACKOFFICE_SUBSCRIPTION_MANAGE: 'Gerenciar assinaturas',
  ROLE_BACKOFFICE_BILLING_MANAGE: 'Gerenciar financeiro',
  ROLE_BACKOFFICE_REPORT_READ: 'Ver relatórios',
  ROLE_BACKOFFICE_AUDIT_READ: 'Ver logs',
  ROLE_BACKOFFICE_EMPLOYEE_READ: 'Ver funcionários',
  ROLE_BACKOFFICE_EMPLOYEE_MANAGE: 'Gerenciar funcionários',
  ROLE_BACKOFFICE_INFLUENCER_READ: 'Ver influenciadores',
  ROLE_BACKOFFICE_INFLUENCER_MANAGE: 'Gerenciar influenciadores',
  ROLE_BACKOFFICE_ACCESS_PROFILE_MANAGE: 'Gerenciar alçadas',
};

/** Human-readable labels for merchant permission codes (admin panel RBAC). */
export const MERCHANT_PERMISSION_LABELS: Record<
  import('./auth').MerchantPermissionCode,
  string
> = {
  ROLE_MERCHANT_DASHBOARD_READ: 'Ver dashboard',
  ROLE_MERCHANT_PROMOTION_READ: 'Ver anúncios',
  ROLE_MERCHANT_PROMOTION_MANAGE: 'Gerenciar anúncios',
  ROLE_MERCHANT_COUPON_VALIDATE: 'Validar cupons',
  ROLE_MERCHANT_COUPON_READ: 'Ver cupons',
  ROLE_MERCHANT_COUPON_MANAGE: 'Gerenciar cupons',
  ROLE_MERCHANT_CUSTOMER_READ: 'Ver clientes',
  ROLE_MERCHANT_REVIEW_READ: 'Ver avaliações',
  ROLE_MERCHANT_REVIEW_MANAGE: 'Responder avaliações',
  ROLE_MERCHANT_CASHBACK_MANAGE: 'Gerenciar cashback',
  ROLE_MERCHANT_FINANCE_READ: 'Ver financeiro',
  ROLE_MERCHANT_FINANCE_MANAGE: 'Gerenciar financeiro',
  ROLE_MERCHANT_REPORT_READ: 'Ver relatórios',
  ROLE_MERCHANT_PROFILE_MANAGE: 'Gerenciar perfil da loja',
  ROLE_MERCHANT_BUSINESS_HOURS_MANAGE: 'Gerenciar horários de atendimento',
  ROLE_MERCHANT_EXPERIENCE_READ: 'Ver experiências',
  ROLE_MERCHANT_EXPERIENCE_MANAGE: 'Gerenciar experiências',
  ROLE_MERCHANT_EMPLOYEE_READ: 'Ver funcionários',
  ROLE_MERCHANT_EMPLOYEE_MANAGE: 'Gerenciar funcionários',
  ROLE_MERCHANT_ACCESS_PROFILE_MANAGE: 'Gerenciar perfis e alçadas',
};

/**
 * The single platform value the front-end has to know by name. The core treats
 * it apart from every other platform: it is the only one that may repeat in the
 * same store and the only one whose label the merchant types. The rest of the
 * list is never declared here - it comes from the catalog endpoint.
 */
export const EXTERNAL_LINK_OTHER_PLATFORM = 'OTHER';

/** Core limits for external links, mirrored so the UI can warn before the request. */
export const EXTERNAL_LINK_MAX_COUNT = 20;
export const EXTERNAL_LINK_MAX_URL_LENGTH = 500;
export const EXTERNAL_LINK_MAX_LABEL_LENGTH = 60;

/** Copy for the `errorCode`s the core returns when an external link is refused. */
export const EXTERNAL_LINK_ERROR_MESSAGES: Record<string, string> = {
  EXTERNAL_LINK_LIMIT: `Cadastre no máximo ${EXTERNAL_LINK_MAX_COUNT} canais.`,
  EXTERNAL_LINK_DUPLICATED: 'Há dois links para o mesmo canal - mantenha apenas um.',
  EXTERNAL_LINK_LABEL_REQUIRED: 'Informe o nome do canal na opção "Outro".',
  EXTERNAL_LINK_LABEL_TOO_LONG: `O nome do canal deve ter no máximo ${EXTERNAL_LINK_MAX_LABEL_LENGTH} caracteres.`,
  EXTERNAL_LINK_URL_REQUIRED: 'Informe o endereço do canal.',
  EXTERNAL_LINK_URL_TOO_LONG: `O endereço deve ter no máximo ${EXTERNAL_LINK_MAX_URL_LENGTH} caracteres.`,
  EXTERNAL_LINK_URL_INVALID: 'Endereço inválido - confira o link do canal.',
  EXTERNAL_LINK_URL_SCHEME: 'Use um endereço que comece com http:// ou https://.',
};

/**
 * How many opening windows the core accepts for a single week day. More than
 * one exists because a store may close for lunch and reopen in the afternoon.
 */
export const BUSINESS_HOUR_MAX_WINDOWS_PER_DAY = 6;

/** Copy for the `errorCode`s the core returns when the opening hours are refused. */
export const BUSINESS_HOUR_ERROR_MESSAGES: Record<string, string> = {
  BUSINESS_HOUR_OVERLAP: 'Há horários que se sobrepõem no mesmo dia - ajuste as faixas.',
  // Fechar antes de abrir agora significa "vira a noite" e é aceito (card 295);
  // o que sobrou de INVERTED é fechar no mesmo minuto em que abre.
  BUSINESS_HOUR_INVERTED: 'O fechamento não pode ser igual à abertura.',
  BUSINESS_HOUR_INVALID_TIME: 'Há um horário digitado fora do formato HH:mm.',
  BUSINESS_HOUR_INCOMPLETE: 'Informe a abertura e o fechamento de cada horário.',
  BUSINESS_HOUR_CLOSED_WITH_WINDOW: 'Dia marcado como fechado não pode ter horário.',
  BUSINESS_HOUR_TOO_MANY_WINDOWS: `Cadastre no máximo ${BUSINESS_HOUR_MAX_WINDOWS_PER_DAY} horários por dia.`,
};

/** Cargo do funcionário da loja. OWNER é super-usuário do próprio lojista. */
export const MERCHANT_ROLE_LABELS: Record<MerchantRole, string> = {
  OWNER: 'Proprietário',
  STAFF: 'Funcionário',
};

/** Situação da conta do funcionário da loja. */
export const MERCHANT_EMPLOYEE_STATUS_LABELS: Record<MerchantEmployeeStatus, string> = {
  ACTIVE: 'Ativo',
  PENDING_ACTIVATION: 'Pendente',
  INACTIVE: 'Inativo',
  BLOCKED: 'Bloqueado',
};

/**
 * Copy para os `errorCode`s que o core devolve ao recusar um funcionário.
 *
 * `EMAIL_ALREADY_EXISTS` está fora da lista de propósito: a mensagem do core
 * nomeia o e-mail em conflito, e é justamente isso que o operador precisa ler
 * para achar a pessoa. Sobrescrever aqui trocaria uma mensagem útil por uma
 * genérica - `extractErrorMessage` cai na do servidor quando o código não está
 * mapeado.
 */
export const MERCHANT_EMPLOYEE_ERROR_MESSAGES: Record<string, string> = {
  ACCESS_PROFILE_REQUIRED:
    'Funcionário com acesso liberado precisa de um perfil de acesso. O lojista cria os perfis no painel dele.',
  ACCESS_PROFILE_NOT_FOUND: 'O perfil de acesso escolhido não pertence a esta loja.',
  EMAIL_ALREADY_EXISTS: 'Já existe um cadastro com esse e-mail. O e-mail é o login e vale para a plataforma inteira.',
  EMPLOYEE_ACCESS_BLOCKED:
    'O acesso deste funcionário está bloqueado - libere o acesso antes de reenviar as credenciais, senão o login recusaria a senha nova.',
  EMPLOYEE_CREDENTIAL_NOT_FOUND:
    'Este funcionário não tem acesso provisionado. Edite o cadastro e libere o acesso para gerar as credenciais.',
  EMAIL_CHANNEL_DISABLED:
    'O envio de e-mail está desligado no servidor, então a senha NÃO foi alterada - sem isso o funcionário ficaria sem a senha antiga e sem a nova. Peça para ligar o canal (app.email.enabled) e tente de novo.',
};
